import React, {
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { compressForUpload } from '../../../../utils/imageCompression';
import {
  stampCaptureExif,
  usableFix,
  type CaptureFix,
} from '../../../../utils/exif/captureExif';
import { useTranslation } from 'react-i18next';

// Sentry
import * as Sentry from '@sentry/react';

// Components
import ModalContainer from '../../containers/ModalContainer';

// Styled Components
import {
  Container,
  ContainerButtons,
  HiddenCanvas,
  PhotoPreview,
  ActionButton,
  DoneButton,
  LargeActionButton,
  BurstCounter,
  Camera,
  ButtonLeftIconAction,
  ButtonRightIconAction,
  CameraContainer,
  LoadingViewport,
} from './styles';

// Types
import { ModalProps } from './types';
import { Img } from '../../../forms/AddBasicInfoAccidentForm/types';
import { Loader } from '../../../inputs/Loader';
import { Typography } from '../../../display/Typography';

// Icons
import { TakePhotoIcon } from '../../../../assets/icons/TakePhotoIcon';
import { CircularArrowsIcon } from '../../../../assets/icons/CircularArrowsIcon';

// Utils
import { generateFileName } from '../../../../utils/files';
import { permissionApiDecides } from '../../../../utils/webPermissions';
import { useTheme } from 'styled-components';

// Cámara
import {
  acquireCamera,
  activeCameraDeviceId,
  deviceSummary,
  errInfo,
  getActiveCameraStream,
  logCam,
  noCameraModalMounted,
  releaseActiveCamera,
  releaseCameraModal,
  retainCameraModal,
} from './cameraManager';
import {
  grabFrame,
  hasUsableFrame,
  makeUniqueName,
  readStoredRatio,
  RESOLUTION_CONSTRAINTS,
  storeRatio,
  toVideoPoint,
  videoConstraintsFor,
} from './capture';
import { useCameraCaps } from './useCameraCaps';
import { useCaptureLocation } from './useCaptureLocation';
import { LocationNoticeBanner } from './parts/LocationNoticeBanner';
import { usePinchZoom } from './usePinchZoom';
import { ZoomControl } from './parts/ZoomControl';
import { TorchButton } from './parts/TorchButton';
import { FocusRing } from './parts/FocusRing';
import { ShotStrip, Shot } from './parts/ShotStrip';
import { CameraPermissionNotice } from './parts/CameraPermissionNotice';
import {
  CameraDenial,
  cameraDenial,
  cameraGranted,
  isPermissionError,
} from './cameraPermission';
import RoisBackgroundLocation from '../../../../plugins/roisBackgroundLocation';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativeApp } from '../../../../utils/positionSource';

/**
 * Fotograma disparado, con lo que había en el INSTANTE del disparo: la hora y
 * la lectura del GPS. Se congelan aquí y no al confirmar con ✓, que puede ser
 * segundos después y con el operario ya en otro sitio.
 */
interface CapturedFrame {
  blob: Blob;
  url: string;
  capturedAt: Date;
  /** `null` si no había una lectura aceptable (ver `usableFix`). */
  fix: CaptureFix | null;
}

/**
 * Foto aceptada que espera en la cámara a «Hecho»: ya comprimida y con su
 * EXIF, lista para entregar al formulario de golpe.
 */
interface StagedShot extends Shot {
  file: File;
  image: Img;
}

/** Fotograma ya congelado cuya codificación sigue en marcha. */
interface GrabbedFrame {
  blob: Promise<Blob>;
  capturedAt: Date;
  fix: CaptureFix | null;
}

/** Zoom al que salta el toque doble, si el dispositivo llega. */
const DOUBLE_TAP_ZOOM = 2;

/**
 * Cuánto hay que mantener pulsado el obturador para que arranque la ráfaga.
 * Por debajo de esto es una foto suelta.
 */
const BURST_HOLD_MS = 450;

/**
 * Cadencia de la ráfaga: 5 fotos por segundo, a ritmo FIJO mientras se
 * mantiene pulsado, como las apps de cámara (decisión del usuario, 12-oct).
 * Antes cada foto esperaba a estar codificada y guardada antes de la
 * siguiente, y con la pantalla ocupada eso eran segundos por foto. Ahora se
 * coge el fotograma al instante y se procesa detrás (ver `grabFrame`).
 */
const BURST_INTERVAL_MS = 200;

/**
 * Fotogramas de la ráfaga que pueden estar esperando a codificarse y
 * guardarse a la vez. Cada uno son ~20 MB de píxeles: si la tablet no da
 * abasto, se salta un turno en vez de acumular memoria hasta que el WebView
 * caiga (`.claude/rules/webview-memoria-apk.md`).
 */
const BURST_MAX_PENDING = 3;

/**
 * Tope de fotos por ráfaga. Un dedo apoyado sin querer no puede llenar la
 * actuación de duplicados ni reventar la cola de subida.
 */
const BURST_MAX_SHOTS = 15;

export const OpenCameraModal = ({
  onClickClose,
  setImagesState,
  setFilesState,
  dataBaseNamingCaching,
  type,
  onImageAdded,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Ref
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  // (El stream vivo lo gestiona `cameraManager` a nivel de módulo. Sobrevive al
  // StrictMode y garantiza una sola apertura a la vez.)

  // States
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [cameraPermission, setCameraPermission] = useState<boolean>(false);
  /** Foto disparada y aún sin decidir. Se superpone al vídeo, que sigue vivo. */
  const [pendingShot, setPendingShot] = useState<CapturedFrame | null>(null);
  /**
   * Fotos aceptadas que esperan a «Hecho» (decisión del usuario, 12-oct). En
   * ref la lista de verdad, que la lee «Hecho» aunque una ráfaga siga
   * añadiendo; en estado, para pintar la tira.
   */
  const [shots, setShots] = useState<StagedShot[]>([]);
  const stagedRef = useRef<StagedShot[]>([]);
  const updateStaged = useCallback(
    (change: (prev: StagedShot[]) => StagedShot[]) => {
      stagedRef.current = change(stagedRef.current);
      setShots(stagedRef.current);
    },
    []
  );
  /** La última ráfaga entera, hasta guardar su última foto: «Hecho» la espera. */
  const processingRef = useRef<Promise<void>>(Promise.resolve());
  const [finishing, setFinishing] = useState(false);
  /**
   * El visor sigue abierto. Cerrarlo sin «Hecho» descarta lo pendiente: lo
   * que termine de procesarse después no puede llegar a ningún sitio.
   */
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [saving, setSaving] = useState(false);
  const [burstActive, setBurstActive] = useState(false);
  /** Fotos cogidas en la ráfaga en curso: el obturador las cuenta. */
  const [burstCount, setBurstCount] = useState(0);
  /**
   * Estado vivo del obturador: en ref porque lo lee un bucle asíncrono.
   *
   * `pointerId` es el dedo que armó el temporizador. Solo ese lo desarma: un
   * segundo dedo (o un guante que marca dos puntos) armaba otro temporizador
   * encima del primero, que se quedaba huérfano y arrancaba una ráfaga que ya
   * nadie podía parar.
   */
  const burstRef = useRef<{
    active: boolean;
    timer: number | null;
    pointerId: number | null;
    /**
     * La ráfaga sigue viva: cogiendo o guardando lo cogido. `active` cae en
     * cuanto se suelta el dedo, pero el bucle aún tarda hasta 200 ms en
     * enterarse y luego guarda lo pendiente; mientras tanto no puede empezar
     * nada más (un rebote del guante colaba otra foto u otra ráfaga).
     */
    running: boolean;
  }>({
    active: false,
    timer: null,
    pointerId: null,
    running: false,
  });
  /**
   * Hay fotos saliendo: la foto suelta (de soltar el dedo a la vista previa) o
   * lo que queda por guardar de una ráfaga ya soltada. Mientras tanto el
   * obturador se ve atenuado e ignora toques, para que nunca se procesen dos
   * fotos a la vez. En ref para el guard, en estado para que el botón lo
   * enseñe.
   */
  const capturingRef = useRef(false);
  const [capturing, setCapturing] = useState(false);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<
    MediaDeviceInfo | undefined
  >(undefined);
  const [selectedCameraIndex, setSelectedCameraIndex] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /**
   * En la APK, sin permiso de cámara: si Android aún lo pregunta (`ask`) o ya
   * solo queda Ajustes (`settings`). Con él, el visor dice qué hacer en vez de
   * solo «Permisos denegados». `null` en el navegador y en cualquier otro error.
   */
  const [denial, setDenial] = useState<CameraDenial | null>(null);
  /**
   * Hay fotograma con dimensiones reales.
   *
   * Se marca con `loadedmetadata`/`canplay`, NO al asignar el `srcObject`:
   * `videoWidth` vale 0 hasta que llegan los metadatos, y con la ráfaga se
   * puede disparar sin esperar a nada. Antes salía un canvas de 0×0.
   */
  const [videoReady, setVideoReady] = useState(false);
  /**
   * Proporción del fotograma real (ancho/alto). Le da la forma al visor para
   * que `object-fit: contain` no tenga que pintar bandas negras.
   *
   * Arranca con la de la última vez para que el modal se abra ya con su tamaño
   * definitivo: si empezara con una supuesta, la caja daría un salto visible en
   * cuanto llegan los metadatos.
   */
  const [videoRatio, setVideoRatio] = useState<number>(readStoredRatio);
  const [focusPoint, setFocusPoint] = useState<{
    x: number;
    y: number;
    at: number;
  } | null>(null);
  // Nonce para RE-adquirir la cámara imperativamente. Ya no hace falta en el
  // camino normal —disparar no para el stream—, pero sí como red de seguridad
  // si Android mata la cámara mientras el operario mira la foto (una llamada
  // entrante, o cambiar de aplicación).
  const [restartNonce, setRestartNonce] = useState(0);

  /** Nombres ya usados en esta sesión — ver `makeUniqueName`. */
  const takenNamesRef = useRef(new Set<string>());
  /** URLs de objeto vivas, para revocarlas todas al cerrar. */
  const objectUrlsRef = useRef<string[]>([]);

  const videoTrack = useMemo(
    () => mediaStream?.getVideoTracks()[0] ?? null,
    [mediaStream]
  );
  const caps = useCameraCaps(videoTrack, RESOLUTION_CONSTRAINTS);
  // GPS para el EXIF de las fotos (spec 2026-09-23): solo con el flag del
  // tenant y el permiso ya concedido; nunca hace esperar al obturador.
  const { currentFix, locationStatus, askLocation } = useCaptureLocation({
    ready: videoReady,
  });

  useLayoutEffect(() => {
    retainCameraModal();
    getCameras();

    return () => {
      releaseCameraModal();
      // Desligar el <video> visualmente (sin PARAR los tracks — el stream lo
      // gestiona el módulo y puede reusarse en el re-montaje de StrictMode).
      if (videoRef.current) videoRef.current.srcObject = null;
      // Soltar la cámara SOLO si es un cierre REAL: el microtask deja pasar el
      // ciclo montar→limpiar→montar de StrictMode; si tras él el contador sigue
      // en 0 → desmontaje de verdad → soltar. Así en dev NO se reabre de más.
      // Parar el track es además lo que apaga la linterna de verdad: hay
      // dispositivos donde `torch: false` no surte efecto.
      queueMicrotask(() => {
        if (noCameraModalMounted()) releaseActiveCamera();
      });
    };
  }, []);

  // Revocar las URLs de objeto al desmontar (las miniaturas de la tira y la
  // foto pendiente). Va en su propio efecto para no mezclarlo con el ciclo de
  // vida de la cámara, que es delicado.
  useEffect(
    () => () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
    },
    []
  );

  // Recuperación automática: si el usuario cambia el permiso de cámara desde los ajustes
  // del navegador con el modal abierto (p.ej. "Restablecer permiso" o activar el toggle),
  // nos enteramos vía el evento 'change' de la Permissions API y reintentamos sin que
  // tenga que cerrar y reabrir el modal. removeEventListener en el cleanup evita fugas.
  useLayoutEffect(() => {
    let permissionStatus: PermissionStatus | null = null;
    const handlePermissionChange = () => {
      if (!permissionStatus) return;
      if (permissionStatus.state === 'denied') {
        setErrorMessage(t('errors:permissionDenied'));
        releaseActiveCamera();
      } else if (!getActiveCameraStream()?.active) {
        // Solo re-intentar si NO tenemos ya cámara viva (recuperación tras
        // desbloquear desde ajustes). Guard clave (fix "5 clics"): el 'change'
        // que dispara el PROPIO grant de permiso re-lanzaba getCameras → más
        // aperturas. Si ya hay stream vivo, no hacemos nada.
        setErrorMessage(null);
        getCameras();
      }
    };
    // En nativo no se escucha: el estado que reporta la Permissions API en el
    // WebView no refleja la decisión del usuario (ver
    // `permissionApiDecides`), y un 'denied' espurio soltaría una cámara
    // que está funcionando.
    if (permissionApiDecides() && navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'camera' as PermissionName })
        .then((status) => {
          permissionStatus = status;
          status.addEventListener('change', handlePermissionChange);
        })
        .catch(() => {
          // Navegador sin soporte de permissions.query('camera').
        });
    }
    return () => {
      permissionStatus?.removeEventListener('change', handlePermissionChange);
    };
  }, []);

  useLayoutEffect(() => {
    let ignore = false;

    const initCamera = async () => {
      if (!selectedCamera) return;

      const activeStream = getActiveCameraStream();
      // REUSO (fix arranque en frío 2026-07-31): si el gestor YA tiene abierto el
      // stream de ESTA cámara (el que abrió getCameras, o el del switch previo),
      // NO reabrimos → una sola apertura física (evita el doble "clic" del
      // objetivo + el NotReadableError de abrir dos veces seguidas). Solo
      // re-asignamos al <video>.
      if (
        activeStream?.active &&
        activeCameraDeviceId() === selectedCamera.deviceId
      ) {
        if (ignore) return;
        logCam(
          'reuso stream ya abierto (sin reabrir)',
          deviceSummary(selectedCamera)
        );
        setMediaStream(activeStream);
        setCameraPermission(true);
        if (videoRef.current) videoRef.current.srcObject = activeStream;
        return;
      }

      // La resolución se pide AL ABRIR, en vez de subir el stream a la máxima
      // del sensor justo después: eso calentaba la tablet sin ganar un píxel
      // útil, porque la foto se recorta a 2560 px antes de subirla.
      const constraints: MediaStreamConstraints = {
        video: videoConstraintsFor(selectedCamera.deviceId || undefined),
      };
      logCam('initCamera → acquireCamera (serializado)', {
        camera: deviceSummary(selectedCamera),
        restartNonce,
        constraints,
      });
      try {
        // `acquireCamera` serializa (nunca dos getUserMedia en vuelo), SUELTA la
        // cámara anterior + respiro, y reintenta si sale NotReadableError. Abre
        // por deviceId con `ideal` (preferencia, no requisito → sin
        // OverconstrainedError si ese id no está).
        const stream = await acquireCamera(constraints);
        logCam('acquireCamera OK', { ignore });
        if (ignore) {
          releaseActiveCamera();
          return;
        }

        setMediaStream(stream);
        setCameraPermission(true);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          logCam('stream asignado al <video>');
        } else {
          logCam('⚠️ videoRef.current NULL al asignar el stream');
        }
      } catch (err) {
        if (!ignore) {
          console.error('[Camera] initCamera ERROR', errInfo(err), err);
          Sentry.captureException(err, {
            tags: { feature: 'camera', phase: 'initCamera' },
            extra: errInfo(err),
          });
          showCameraError(err);
        }
      }
    };

    initCamera();

    return () => {
      ignore = true;
      // NO paramos el stream aquí: el gestor de módulo es el dueño y lo suelta
      // en el siguiente `acquireCamera` (con su respiro) o al cerrar el modal
      // (cleanup del efecto de montaje). Aquí solo desligamos el <video>
      // visualmente. Si lo paráramos, el respiro de acquire no aplicaría
      // (vería la cámara ya libre) y el switch volvería a dar NotReadableError.
      if (videoRef.current) videoRef.current.srcObject = null;
      setVideoReady(false);
      // La proporción NO se resetea: al cambiar de cámara, dejarla en el valor
      // conocido evita que el visor encoja y vuelva a crecer entre medias.
    };
    // BUG #6+10 FIX: Remove cameraPermission from deps - it caused re-entry loop
    // when setCameraPermission(true) was called inside initCamera.
  }, [selectedCamera, restartNonce]);

  // Traduce el error técnico de la cámara a un mensaje legible para el usuario de
  // campo (el detalle crudo ya queda en Sentry). Evita mostrar "OverconstrainedError"
  // o "NotAllowedError" en pantalla.
  const cameraErrorMessage = (err: unknown): string => {
    const name = (err as { name?: string })?.name;
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return t('errors:permissionDenied');
    }
    // El detalle crudo del error queda en Sentry (no en pantalla).
    return t('errors:generalError');
  };

  /**
   * Enseña el error de la cámara y, si es de permiso en la APK, averigua qué
   * puede hacer el operario (ver `cameraDenial`).
   */
  const showCameraError = (err: unknown): void => {
    setErrorMessage(cameraErrorMessage(err));
    if (isPermissionError(err)) {
      void cameraDenial().then(setDenial);
    }
  };

  const getCameras = async () => {
    logCam('getCameras start', {
      isSecureContext: window.isSecureContext,
      hasMediaDevices: !!navigator.mediaDevices,
      userAgent: navigator.userAgent,
    });
    try {
      // Detección proactiva (Permissions API): si el permiso de cámara está BLOQUEADO,
      // avisamos con un mensaje claro en vez de intentar abrir la cámara y dejar un feed
      // roto. El listener de CAMBIOS (recuperación automática al desbloquear) vive en su
      // propio effect. La query de 'camera' no está soportada en todos los navegadores
      // (p.ej. Firefox) → si falla, seguimos el flujo normal (getUserMedia gestiona igual).
      try {
        const status = permissionApiDecides()
          ? await navigator.permissions?.query({
              name: 'camera' as PermissionName,
            })
          : undefined;
        logCam('permissions.query(camera)', status?.state);
        if (status?.state === 'denied') {
          setErrorMessage(t('errors:permissionDenied'));
          return;
        }
      } catch {
        // Navegador sin soporte de permissions.query('camera') → flujo normal.
        logCam('permissions.query(camera) no soportado');
      }

      let devices = await navigator.mediaDevices.enumerateDevices();
      let cameraDevices = devices.filter(
        (device) => device.kind === 'videoinput'
      );
      logCam('enumerateDevices #1', cameraDevices.map(deviceSummary));

      // Chrome oculta deviceId/label de las cámaras hasta que el permiso está
      // concedido (protección de privacidad). Si vienen vacíos, enumerar no
      // basta: abrimos la cámara (dispara el prompt) para desbloquearlos.
      // Fix Samsung 2026-07-31: NO cerramos ese stream — lo REUSAMOS como cámara
      // inicial (vía el gestor `acquireCamera`), así initCamera no reabre → una
      // sola apertura física en frío (sin doble "clic" ni NotReadableError por
      // abrir dos veces seguidas). Preferimos la trasera (`facingMode`).
      const needsPermission =
        cameraDevices.length === 0 || cameraDevices.some((d) => !d.deviceId);
      logCam('needsPermission (abrir + reusar)', needsPermission);
      if (needsPermission) {
        await acquireCamera({
          video: {
            facingMode: { ideal: 'environment' },
            ...RESOLUTION_CONSTRAINTS,
          },
        });
        logCam('cámara inicial abierta (se reusa)', activeCameraDeviceId());
        devices = await navigator.mediaDevices.enumerateDevices();
        cameraDevices = devices.filter((device) => device.kind === 'videoinput');
        logCam(
          'enumerateDevices #2 (post-permiso)',
          cameraDevices.map(deviceSummary)
        );
      }

      // Elegir la cámara: la que YA abrimos (para que initCamera la reuse sin
      // reabrir), o la trasera por label, o la primera.
      const openId = activeCameraDeviceId();
      const openCamera = openId
        ? cameraDevices.find((d) => d.deviceId === openId)
        : undefined;
      const cameraBack = cameraDevices.find((d) => d.label.includes('back'));
      const chosen = openCamera ?? cameraBack ?? cameraDevices[0];
      const chosenIndex = Math.max(
        0,
        cameraDevices.findIndex((d) => d.deviceId === chosen?.deviceId)
      );
      logCam('getCameras → elegida', {
        elegida: chosen ? deviceSummary(chosen) : '(ninguna)',
        reusada: !!openCamera,
        total: cameraDevices.length,
      });
      setSelectedCameraIndex(chosenIndex);
      setCameraPermission(true);
      setCameras(cameraDevices);
      setSelectedCamera(chosen);
    } catch (err) {
      console.error('[Camera] getCameras ERROR', errInfo(err), err);
      Sentry.captureException(err, {
        tags: { feature: 'camera', phase: 'getCameras' },
        extra: errInfo(err),
      });
      showCameraError(err);
    }
  };

  /**
   * Vuelve a abrir la cámara desde el aviso de permiso: es `getUserMedia` lo
   * que hace salir el diálogo de Android. Solo desde el botón o tras
   * comprobar que ya hay permiso (ver el efecto de `resume`).
   */
  const retryCamera = (): void => {
    setDenial(null);
    setErrorMessage(null);
    void getCameras();
  };
  const retryCameraRef = useRef(retryCamera);
  retryCameraRef.current = retryCamera;

  const openCameraSettings = (): void => {
    void RoisBackgroundLocation.openAppSettings().catch(() => {
      // Sin Ajustes no hay nada que ofrecer: el aviso sigue y el operario
      // tendrá que llegar a mano.
    });
  };

  // Al volver de Ajustes con el aviso de permiso en pantalla: si ya hay
  // permiso, la cámara se abre sola. 🔴 Solo se COMPRUEBA: pedirlo desde el
  // `resume` sería un bucle, porque con el permiso negado para siempre Android
  // contesta al instante pero la app pasa igual por pausa y vuelta.
  useEffect(() => {
    if (denial !== 'settings' || !isNativeApp()) return undefined;
    let handle: { remove: () => Promise<void> } | null = null;
    let cancelled = false;
    void CapacitorApp.addListener('resume', () => {
      void cameraGranted().then((granted) => {
        if (granted && !cancelled) retryCameraRef.current();
      });
    }).then((h) => {
      if (cancelled) {
        void h.remove();
        return;
      }
      handle = h;
    });
    return () => {
      cancelled = true;
      if (handle) void handle.remove();
    };
  }, [denial]);

  /**
   * Hay fotograma utilizable: se habilita el disparador y el visor toma su
   * forma. Se ata a `loadedmetadata` y a `canplay` porque `videoWidth` vale 0
   * hasta que llegan los metadatos.
   */
  const handleVideoReady = useCallback(() => {
    const video = videoRef.current;
    if (!hasUsableFrame(video) || !video) return;
    setVideoReady(true);
    const ratio = video.videoWidth / video.videoHeight;
    setVideoRatio(ratio);
    storeRatio(ratio);
  }, []);

  /**
   * Congela el fotograma actual, con la hora y la lectura del GPS de ese mismo
   * instante, y deja su codificación en marcha.
   */
  const grabShot = useCallback(async (): Promise<GrabbedFrame | null> => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    // `hasUsableFrame` y no `videoReady` a secas: es la comprobación que de
    // verdad impide un canvas de 0×0.
    if (!video || !canvas || !hasUsableFrame(video)) return null;
    // Antes de codificar: codificar tarda, y el instante que cuenta es el del
    // disparo. La lectura se evalúa, no se espera (D3/D4).
    const capturedAt = new Date();
    const fix = usableFix(currentFix(), capturedAt.getTime());
    const { blob } = await grabFrame(video, canvas, caps.digitalZoom);
    return { blob, capturedAt, fix };
  }, [caps.digitalZoom, currentFix]);

  /** Espera a la codificación y le pone la URL de vista. */
  const finishShot = useCallback(
    async (grabbed: GrabbedFrame): Promise<CapturedFrame> => {
      const blob = await grabbed.blob;
      const url = URL.createObjectURL(blob);
      // Con el visor ya cerrado nadie la va a revocar: se suelta aquí.
      if (mountedRef.current) objectUrlsRef.current.push(url);
      else URL.revokeObjectURL(url);
      return { blob, url, capturedAt: grabbed.capturedAt, fix: grabbed.fix };
    },
    []
  );

  /** Coge el fotograma actual y espera a tenerlo listo (la foto suelta). */
  const captureFrame = useCallback(async (): Promise<CapturedFrame | null> => {
    const grabbed = await grabShot();
    return grabbed ? finishShot(grabbed) : null;
  }, [grabShot, finishShot]);

  /**
   * Deja la foto lista en la tira de la cámara, a la espera de «Hecho».
   *
   * Lo comparten el camino normal (aceptar con ✓) y la ráfaga, que guarda
   * directamente sin pasar por la confirmación. El formulario NO se entera
   * hasta «Hecho»: así la lista de detrás no cambia foto a foto ni se
   * redibuja con cada una.
   */
  const saveShot = useCallback(
    async (shot: CapturedFrame) => {
      // Visor cerrado sin «Hecho»: la foto está descartada, no se procesa.
      if (!mountedRef.current) return;
      const creationDate = new Date();
      const baseName = makeUniqueName(
        generateFileName(type as string, dataBaseNamingCaching as string),
        takenNamesRef.current
      );
      takenNamesRef.current.add(baseName);

      const file = new File([shot.blob], `${baseName}.jpeg`, {
        type: 'image/jpeg',
      });

      // Compresión SIEMPRE, no solo por encima de un umbral: antes una foto de
      // 2,8 MB pasaba intacta y 20 de ellas son ~60 MB, que con cobertura mala
      // son ~25 minutos de subida. Ahora la captura ya sale dentro del tope, así
      // que lo normal es que esto la deje intacta en vez de recodificarla.
      const compressed = await compressForUpload(file);

      // EXIF de captura (fecha, software y, si hay lectura válida, GPS) sobre
      // el fichero YA comprimido: así esta compresión no puede perderlo. Si
      // falla, devuelve la foto tal cual — nunca se pierde una foto por esto.
      const fileToSave = await stampCaptureExif(compressed, {
        capturedAt: shot.capturedAt,
        fix: shot.fix,
      });

      const image: Img = {
        id: fileToSave.name,
        date: creationDate.toISOString(),
        name: fileToSave.name,
      };
      updateStaged((prev) => [
        ...prev,
        { id: fileToSave.name, previewUrl: shot.url, file: fileToSave, image },
      ]);
    },
    [type, dataBaseNamingCaching, updateStaged]
  );

  /**
   * Desarma el obturador: temporizador, dedo y ráfaga.
   *
   * Hace falta cada vez que el obturador desaparece de la pantalla (vista
   * previa, error, cierre): el «levantar» del dedo ya no tiene a quién llegar,
   * y sin esto un temporizador pendiente arrancaba una ráfaga imparable.
   */
  const cancelPress = useCallback(() => {
    const press = burstRef.current;
    if (press.timer !== null) {
      window.clearTimeout(press.timer);
      press.timer = null;
    }
    press.pointerId = null;
    press.active = false;
  }, []);

  const takePhoto = useCallback(async () => {
    const press = burstRef.current;
    if (pendingShot || press.active || press.running || capturingRef.current) return;
    capturingRef.current = true;
    setCapturing(true);
    try {
      const shot = await captureFrame();
      if (shot) {
        // La vista previa sustituye al obturador: nada armado puede seguir.
        cancelPress();
        setPendingShot(shot);
      }
    } catch (err) {
      console.error('[Camera] takePhoto ERROR', errInfo(err), err);
      Sentry.captureException(err, {
        tags: { feature: 'camera', phase: 'takePhoto' },
        extra: errInfo(err),
      });
      setErrorMessage(t('errors:generalError'));
    } finally {
      capturingRef.current = false;
      setCapturing(false);
    }
  }, [captureFrame, cancelPress, pendingShot, t]);

  const reportBurstError = useCallback(
    (err: unknown) => {
      console.error('[Camera] ráfaga ERROR', errInfo(err), err);
      Sentry.captureException(err, {
        tags: { feature: 'camera', phase: 'burst' },
        extra: errInfo(err),
      });
      setErrorMessage(t('errors:generalError'));
    },
    [t]
  );

  /**
   * Ráfaga: mientras se mantiene pulsado el obturador, dispara solo.
   *
   * Como las apps de cámara: fotogramas a ritmo FIJO (`BURST_INTERVAL_MS`)
   * mientras el dedo sigue apoyado, y el obturador cuenta los que lleva. Cada
   * fotograma se congela al instante y se codifica y guarda DETRÁS, de uno en
   * uno; al soltar se deja de coger, pero lo cogido se termina de guardar.
   *
   * Las fotos se guardan DIRECTAMENTE, sin la confirmación de ✓/✗: confirmar
   * una a una anularía el sentido de la ráfaga. Lo que se descarta se quita
   * luego desde la tira, que para eso está.
   *
   * Si la tablet no da abasto (`BURST_MAX_PENDING` esperando), se salta un
   * turno: hace menos fotos en vez de acumular memoria hasta ahogarse.
   */
  const runBurst = useCallback(async () => {
    const press = burstRef.current;
    if (press.active || press.running) return;
    press.active = true;
    press.running = true;
    // «Hecho» espera a la ráfaga ENTERA, no solo a lo que ya se está
    // guardando: si se pulsa mientras se coge un fotograma, ese también entra.
    let finishBurst: () => void = () => undefined;
    processingRef.current = new Promise<void>((resolve) => {
      finishBurst = resolve;
    });
    setBurstActive(true);
    setBurstCount(0);
    let taken = 0;
    let pending = 0;
    let saving: Promise<void> = Promise.resolve();
    try {
      while (press.active && taken < BURST_MAX_SHOTS) {
        const t0 = performance.now();
        if (pending < BURST_MAX_PENDING) {
          const grabbed = await grabShot();
          if (!grabbed) break;
          taken += 1;
          pending += 1;
          setBurstCount(taken);
          saving = saving
            .then(async () => saveShot(await finishShot(grabbed)))
            .catch(reportBurstError)
            .finally(() => {
              pending -= 1;
            });
        }
        // `performance.now` y no `Date.now`: si el reloj del aparato salta
        // hacia atrás, la espera no puede volverse eterna.
        const remaining = BURST_INTERVAL_MS - (performance.now() - t0);
        if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
      }
    } catch (err) {
      reportBurstError(err);
    } finally {
      press.active = false;
      setBurstActive(false);
    }
    // Lo cogido se termina de guardar aunque ya se haya soltado.
    try {
      if (pending > 0) {
        capturingRef.current = true;
        setCapturing(true);
        await saving;
        capturingRef.current = false;
        setCapturing(false);
      }
    } finally {
      press.running = false;
      finishBurst();
    }
  }, [grabShot, finishShot, saveShot, reportBurstError]);

  /**
   * Empieza a contar para distinguir un toque suelto de una ráfaga.
   *
   * 🔴 Mientras sale la foto anterior no se arma nada. Era el camino del botón
   * «pillado»: el operario volvía a pulsar porque la foto tardaba, la vista
   * previa aparecía con su dedo apoyado, el obturador desaparecía y el
   * temporizador arrancaba una ráfaga que nadie paraba (15 fotos guardadas
   * por detrás de la vista previa, medido en la tablet el 12-oct).
   */
  const handleShutterDown = useCallback(
    (event: React.PointerEvent) => {
      if (pendingShot || !videoReady || errorMessage) return;
      const press = burstRef.current;
      // Un segundo dedo se ignora solo si el primero sigue VIVO (contando o en
      // ráfaga). Mirar solo `pointerId` dejaría el obturador muerto para
      // siempre si algún «levantar» se perdiera.
      if (
        finishing ||
        capturingRef.current ||
        press.active ||
        press.running ||
        press.timer !== null
      ) {
        return;
      }
      press.pointerId = event.pointerId;
      press.timer = window.setTimeout(() => {
        press.timer = null;
        void runBurst();
      }, BURST_HOLD_MS);
    },
    [pendingShot, videoReady, errorMessage, finishing, runBurst]
  );

  /** Al soltar: si no llegó a ráfaga, es una foto suelta. */
  const handleShutterUp = useCallback(
    (event: React.PointerEvent) => {
      const press = burstRef.current;
      // Solo cuenta el dedo que pulsó. Esto también descarta el
      // `pointerleave` que llega justo detrás del `pointerup` del mismo dedo.
      if (press.pointerId === null || event.pointerId !== press.pointerId) return;
      press.pointerId = null;
      if (press.timer !== null) {
        window.clearTimeout(press.timer);
        press.timer = null;
        void takePhoto();
        return;
      }
      press.active = false;
    },
    [takePhoto]
  );

  // Un error también se lleva el obturador (y el cierre del modal, todo).
  useEffect(() => {
    if (errorMessage) cancelPress();
  }, [errorMessage, cancelPress]);
  useEffect(() => cancelPress, [cancelPress]);

  /**
   * «Hecho»: entrega TODAS las fotos de la tira al formulario, en el orden en
   * que se hicieron, y cierra.
   *
   * Si una ráfaga aún está guardando, primero la espera: lo que se ha cogido
   * se entrega entero. Cerrar de cualquier otra forma (X, Escape, tocar fuera)
   * no pasa por aquí y descarta lo pendiente, igual que en el tramo del
   * recorrido (decisión del usuario, 12-oct).
   */
  const handleDone = useCallback(async () => {
    if (finishing) return;
    setFinishing(true);
    cancelPress();
    // Una ráfaga puede encadenar otro guardado mientras se espera.
    let waited: Promise<void>;
    do {
      waited = processingRef.current;
      await waited;
    } while (waited !== processingRef.current);
    if (!mountedRef.current) return;

    const staged = stagedRef.current;
    if (staged.length > 0) {
      setFilesState((prevFiles) => [
        ...prevFiles,
        ...staged.map(({ id, file }) => ({ id, file })),
      ]);
      setImagesState((prevImages) => [
        ...prevImages,
        ...staged.map(({ image }) => image),
      ]);
      // Registrar cada foto en el change-tracking del flujo de edición (si
      // aplica). Sin esto, en ActionsValidation la foto de cámara quedaba en
      // pantalla y en OPFS pero NO en DATA_CHANGES.editionData → el save no
      // la subía.
      staged.forEach(({ image }) => onImageAdded?.(image));
    }
    onClickClose();
  }, [finishing, cancelPress, setFilesState, setImagesState, onImageAdded, onClickClose]);

  /**
   * Acepta la foto pendiente: pasa a la tira y se VUELVE al visor. Al
   * formulario llega con «Hecho», junto con las demás.
   */
  const acceptPhoto = useCallback(async () => {
    if (!pendingShot || saving) return;
    setSaving(true);
    try {
      await saveShot(pendingShot);
      setPendingShot(null);
    } catch (err) {
      console.error('[Camera] acceptPhoto ERROR', errInfo(err), err);
      Sentry.captureException(err, {
        tags: { feature: 'camera', phase: 'acceptPhoto' },
        extra: errInfo(err),
      });
      setErrorMessage(t('errors:generalError'));
    } finally {
      setSaving(false);
    }
  }, [pendingShot, saving, saveShot, t]);

  /**
   * Descarta una foto de la tira. Aún no está en el formulario ni en el
   * almacenamiento (eso pasa con «Hecho»), así que basta con soltarla aquí.
   */
  const removeShot = useCallback(
    (id: string) => {
      const removed = stagedRef.current.find((shot) => shot.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
        objectUrlsRef.current = objectUrlsRef.current.filter(
          (url) => url !== removed.previewUrl
        );
      }
      updateStaged((prev) => prev.filter((shot) => shot.id !== id));
      // El nombre NO se saca de `takenNamesRef`: dos fotos de la misma tanda
      // no deben poder compartir nombre aunque una se haya descartado.
    },
    [updateStaged]
  );

  /** Rechaza la foto pendiente y vuelve al visor. */
  const rejectPhoto = useCallback(() => {
    if (!pendingShot) return;
    URL.revokeObjectURL(pendingShot.url);
    objectUrlsRef.current = objectUrlsRef.current.filter(
      (url) => url !== pendingShot.url
    );
    setPendingShot(null);
    // Red de seguridad: si Android se llevó la cámara mientras el operario
    // miraba la foto, re-adquirirla. En el camino normal el stream sigue vivo y
    // esto no hace nada.
    if (!getActiveCameraStream()?.active) setRestartNonce((n) => n + 1);
  }, [pendingShot]);

  const changeCamera = useCallback(() => {
    if (cameras.length > 1) {
      const nextCameraId = (selectedCameraIndex + 1) % cameras.length;
      const newCamera = cameras.find(
        (camera) => camera.deviceId === cameras[nextCameraId].deviceId
      );
      logCam('changeCamera', {
        de: deviceSummary(cameras[selectedCameraIndex]),
        a: newCamera ? deviceSummary(newCamera) : '(no encontrada)',
        streamAnteriorVivo: !!mediaStream?.active,
      });
      setSelectedCameraIndex(nextCameraId);
      setSelectedCamera(newCamera);
    }
  }, [cameras, selectedCameraIndex, mediaStream]);

  /** Enfoca donde se ha tocado, si el dispositivo sabe hacerlo. */
  const handleTap = useCallback(
    (clientX: number, clientY: number) => {
      const video = videoRef.current;
      const box = viewportRef.current;
      if (!video || !box || !caps.focus.supported || !hasUsableFrame(video)) {
        return;
      }
      const rect = box.getBoundingClientRect();
      const point = toVideoPoint(
        clientX,
        clientY,
        rect,
        video.videoWidth,
        video.videoHeight,
        caps.digitalZoom
      );
      // Fuera de la imagen (en las bandas del `contain`): no es un punto de la
      // escena, así que no se le manda nada al sensor.
      if (!point) return;
      caps.focus.focusAt(point.x, point.y);
      setFocusPoint({
        x: clientX - rect.left,
        y: clientY - rect.top,
        at: Date.now(),
      });
    },
    [caps.focus, caps.digitalZoom]
  );

  const handleDoubleTap = useCallback(() => {
    const { factor, maxFactor, setFactor } = caps.zoom;
    setFactor(factor > 1 ? 1 : Math.min(DOUBLE_TAP_ZOOM, maxFactor));
  }, [caps.zoom]);

  const gestures = usePinchZoom({
    factor: caps.zoom.factor,
    onZoom: caps.zoom.setFactor,
    onTap: handleTap,
    onDoubleTap: handleDoubleTap,
    disabled: !!pendingShot || !!errorMessage,
  });

  // El anillo de enfoque se apaga solo; sin esto quedaría clavado en pantalla.
  useEffect(() => {
    if (!focusPoint) return;
    const id = setTimeout(() => setFocusPoint(null), 900);
    return () => clearTimeout(id);
  }, [focusPoint]);

  const showViewfinder = cameraPermission && !errorMessage;

  // Sin permiso de cámara en la APK: un aviso NUESTRO, así que con el aspecto
  // de los demás modales (tarjeta blanca, como «Añadir multimedia», de donde
  // se viene) y no dentro del visor negro de la cámara.
  if (denial) {
    return (
      <ModalContainer
        width={40}
        onClickClose={onClickClose}
        fill={theme.colors.black}
      >
        <CameraPermissionNotice
          mode={denial}
          onContinue={retryCamera}
          onOpenSettings={openCameraSettings}
        />
      </ModalContainer>
    );
  }

  return (
    <ModalContainer
      width={72}
      modalType={'camera'}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      <Container {...props}>
        {errorMessage ? (
          // El error se muestra SIEMPRE dentro del visor negro (no como texto suelto
          // sobre el fondo claro del modal): texto en blanco para contrastar. Los
          // controles siguen visibles pero deshabilitados (no hay cámara que capturar).
          <LoadingViewport>
            <Typography colorText="white" size="L">
              {errorMessage}
            </Typography>
          </LoadingViewport>
        ) : !showViewfinder ? (
          <LoadingViewport>
            <Loader />
          </LoadingViewport>
        ) : (
          <CameraContainer ref={viewportRef} $ratio={videoRatio} {...gestures}>
            <Camera
              ref={videoRef}
              autoPlay
              playsInline
              muted
              $zoom={caps.digitalZoom}
              onLoadedMetadata={handleVideoReady}
              onCanPlay={handleVideoReady}
            />

            {caps.torch.supported && !pendingShot && (
              <TorchButton
                on={caps.torch.on}
                label={t(
                  caps.torch.on ? 'modals:camera.torchOff' : 'modals:camera.torchOn'
                )}
                onToggle={caps.torch.toggle}
              />
            )}

            {!pendingShot && (
              <ZoomControl
                factor={caps.zoom.factor}
                maxFactor={caps.zoom.maxFactor}
                label={t('modals:camera.zoom')}
                onSelect={caps.zoom.setFactor}
              />
            )}

            {focusPoint && !pendingShot && (
              <FocusRing x={focusPoint.x} y={focusPoint.y} />
            )}

            {/* Aviso de que la próxima foto no llevará ubicación (§ 15). Nunca
                frena el disparo. */}
            {!pendingShot && (
              <LocationNoticeBanner status={locationStatus} onAsk={askLocation} />
            )}

            {!pendingShot && (
              <ShotStrip
                shots={shots}
                label={t('modals:camera.shots')}
                photoAlt={t('modals:camera.photoTaken')}
                removeLabel={t('modals:camera.discard')}
                onRemove={removeShot}
              />
            )}

            {pendingShot && (
              <PhotoPreview
                src={pendingShot.url}
                alt={t('modals:camera.photoTaken')}
              />
            )}
          </CameraContainer>
        )}

        <ContainerButtons>
          {pendingShot ? (
            <>
              <ButtonLeftIconAction
                variant="check"
                onClick={acceptPhoto}
                disabled={saving}
              />
              <ButtonRightIconAction
                variant="cancel"
                onClick={rejectPhoto}
                disabled={saving}
              />
            </>
          ) : (
            <>
              {shots.length > 0 && (
                <DoneButton onClick={handleDone} disabled={finishing}>
                  {t('modals:camera.done')}
                </DoneButton>
              )}
              {/* El disparo va en el SOLTAR, no en `onClick`: es lo que
                  permite distinguir un toque suelto de mantener pulsado para
                  la ráfaga. `onPointerLeave` cubre que el dedo se deslice
                  fuera del botón sin levantarlo. */}
              <LargeActionButton
                $burst={burstActive}
                onPointerDown={handleShutterDown}
                onPointerUp={handleShutterUp}
                onPointerLeave={handleShutterUp}
                onPointerCancel={handleShutterUp}
                disabled={!!errorMessage || !videoReady || capturing || finishing}
                aria-label={t('modals:camera.shutter')}
              >
                <TakePhotoIcon />
                {burstActive && burstCount > 0 && (
                  <BurstCounter aria-hidden>{burstCount}</BurstCounter>
                )}
              </LargeActionButton>
              {cameras.length > 1 && (
                <ActionButton onClick={changeCamera} disabled={!!errorMessage}>
                  <CircularArrowsIcon />
                </ActionButton>
              )}
            </>
          )}
        </ContainerButtons>
        <HiddenCanvas ref={canvasRef} />
      </Container>
    </ModalContainer>
  );
};
